import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from './firebase';
import './AdminFeeStructureSetup.css';

function AdminFeeStructureSetup({ onClose }) {
  // Default values
  const [defaultAdminFee, setDefaultAdminFee] = useState(160);
  
  // Breakdown - Fixed amounts
  const [bbFund, setBbFund] = useState(20);
  const [franchiseeKeeps, setFranchiseeKeeps] = useState(20);
  const [headOfficeGets, setHeadOfficeGets] = useState(120);
  
  // T-shirt settings
  const [includesTshirt, setIncludesTshirt] = useState(true);
  const [tshirtCostToHO, setTshirtCostToHO] = useState(110);
  
  // Waiver rules
  const [zeroFeeRules, setZeroFeeRules] = useState({
    bb: 0,
    franchisee: 0,
    ho: 0,
    tshirt: false
  });
  
  const [partial40Rules, setPartial40Rules] = useState({
    bb: 10,
    franchisee: 10,
    ho: 20,
    tshirt: false
  });
  
  // Additional pricing
  const [adultSmallLarge, setAdultSmallLarge] = useState(130);
  const [adultXlUp, setAdultXlUp] = useState(140);
  const [stickerPrice, setStickerPrice] = useState(70);
  
  // Royalty settings
  const [royaltyPct, setRoyaltyPct] = useState(10);
  const [vatPct, setVatPct] = useState(15);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCurrentStructure();
  }, []);

  const fetchCurrentStructure = async () => {
    try {
      const structureDoc = await getDoc(doc(db, 'adminFeeStructure', 'current'));
      
      if (structureDoc.exists()) {
        const data = structureDoc.data();
        setDefaultAdminFee(data.defaultAdminFee || 160);
        setBbFund(data.brandBuildingFund || 20);
        setFranchiseeKeeps(data.franchiseeKeeps || 20);
        setHeadOfficeGets(data.headOfficeGets || 120);
        setIncludesTshirt(data.includesTshirt !== undefined ? data.includesTshirt : true);
        setTshirtCostToHO(data.tshirtCostToHO || 110);
        
        if (data.waiverRules) {
          setZeroFeeRules(data.waiverRules.zero || zeroFeeRules);
          setPartial40Rules(data.waiverRules.partial40 || partial40Rules);
        }
        
        if (data.adultTshirtPricing) {
          setAdultSmallLarge(data.adultTshirtPricing.smallToLarge || 130);
          setAdultXlUp(data.adultTshirtPricing.xlToXxxxl || 140);
        }
        
        setStickerPrice(data.stickerPricePerRoll || 70);
        setRoyaltyPct(data.royaltyPercentage || 10);
        setVatPct(data.vatPercentage || 15);
      }
    } catch (error) {
      console.error('Error fetching admin fee structure:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-calculate head office amount when other values change
  useEffect(() => {
    const calculated = parseFloat(defaultAdminFee) - parseFloat(bbFund) - parseFloat(franchiseeKeeps);
    setHeadOfficeGets(Math.max(0, calculated));
  }, [defaultAdminFee, bbFund, franchiseeKeeps]);

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const structureData = {
        // Default values
        defaultAdminFee: parseFloat(defaultAdminFee),
        
        // Breakdown
        brandBuildingFund: parseFloat(bbFund),
        franchiseeKeeps: parseFloat(franchiseeKeeps),
        headOfficeGets: parseFloat(headOfficeGets),
        
        // T-shirt settings
        includesTshirt: includesTshirt,
        tshirtCostToHO: parseFloat(tshirtCostToHO),
        
        // Waiver rules
        waiverRules: {
          zero: {
            bb: parseFloat(zeroFeeRules.bb),
            franchisee: parseFloat(zeroFeeRules.franchisee),
            ho: parseFloat(zeroFeeRules.ho),
            tshirt: zeroFeeRules.tshirt
          },
          partial40: {
            bb: parseFloat(partial40Rules.bb),
            franchisee: parseFloat(partial40Rules.franchisee),
            ho: parseFloat(partial40Rules.ho),
            tshirt: partial40Rules.tshirt
          }
        },
        
        // Adult T-shirt pricing
        adultTshirtPricing: {
          smallToLarge: parseFloat(adultSmallLarge),
          xlToXxxxl: parseFloat(adultXlUp)
        },
        
        // Stickers
        stickerPricePerRoll: parseFloat(stickerPrice),
        
        // Royalty settings
        royaltyPercentage: parseFloat(royaltyPct),
        vatPercentage: parseFloat(vatPct),
        
        // Audit
        lastUpdated: Timestamp.now(),
        updatedBy: auth.currentUser?.uid || 'system'
      };
      
      await setDoc(doc(db, 'adminFeeStructure', 'current'), structureData);
      
      alert('Admin fee structure saved successfully!');
      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving admin fee structure:', error);
      alert('Error saving configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const validateBreakdown = () => {
    const total = parseFloat(bbFund) + parseFloat(franchiseeKeeps) + parseFloat(headOfficeGets);
    return Math.abs(total - parseFloat(defaultAdminFee)) < 0.01; // Allow for floating point precision
  };

  if (loading) {
    return <div className="admin-fee-setup loading">Loading configuration...</div>;
  }

  return (
    <div className="admin-fee-setup">
      <div className="setup-header">
        <h2>Admin Fee Structure Configuration</h2>
        <p>Configure how admin fees are split between Brand Building, Franchisee, and Head Office</p>
      </div>

      {/* Default Admin Fee */}
      <section className="setup-section">
        <h3>Standard Admin Fee</h3>
        <div className="form-group">
          <label>
            Default Admin Fee Amount (when school doesn't customize):
            <input 
              type="number" 
              value={defaultAdminFee}
              onChange={(e) => setDefaultAdminFee(e.target.value)}
              min="0"
              step="1"
            />
            <span className="currency">Rands (R)</span>
          </label>
        </div>
      </section>

      {/* Breakdown */}
      <section className="setup-section breakdown-section">
        <h3>Fee Breakdown for R{defaultAdminFee} Admin Fee</h3>
        
        <div className="breakdown-grid">
          <div className="form-group">
            <label>
              Brand Building Fund:
              <input 
                type="number" 
                value={bbFund}
                onChange={(e) => setBbFund(e.target.value)}
                min="0"
                step="1"
              />
              <span className="currency">R{bbFund}</span>
            </label>
          </div>
          
          <div className="form-group">
            <label>
              Franchisee Keeps:
              <input 
                type="number" 
                value={franchiseeKeeps}
                onChange={(e) => setFranchiseeKeeps(e.target.value)}
                min="0"
                step="1"
              />
              <span className="currency">R{franchiseeKeeps}</span>
            </label>
          </div>
          
          <div className="form-group">
            <label>
              Head Office Gets (Auto-calculated):
              <input 
                type="number" 
                value={headOfficeGets}
                disabled
                className="auto-calculated"
              />
              <span className="currency">R{headOfficeGets}</span>
            </label>
          </div>
        </div>
        
        <div className={`total-check ${validateBreakdown() ? 'valid' : 'invalid'}`}>
          <strong>Total:</strong> R{(parseFloat(bbFund) + parseFloat(franchiseeKeeps) + parseFloat(headOfficeGets)).toFixed(2)}
          {!validateBreakdown() && (
            <span className="warning">⚠️ Does not equal R{defaultAdminFee}</span>
          )}
          {validateBreakdown() && (
            <span className="success">✓ Breakdown matches admin fee</span>
          )}
        </div>
      </section>

      {/* T-shirt Settings */}
      <section className="setup-section">
        <h3>T-shirt Settings</h3>
        
        <div className="form-group checkbox-group">
          <label>
            <input 
              type="checkbox"
              checked={includesTshirt}
              onChange={(e) => setIncludesTshirt(e.target.checked)}
            />
            Admin fee includes T-shirt for student
          </label>
        </div>
        
        {includesTshirt && (
          <div className="form-group">
            <label>
              T-shirt cost to Head Office (from admin fee):
              <input 
                type="number" 
                value={tshirtCostToHO}
                onChange={(e) => setTshirtCostToHO(e.target.value)}
                min="0"
                step="1"
              />
              <span className="currency">R{tshirtCostToHO}</span>
            </label>
            <small>This is how much of the R{headOfficeGets} goes toward T-shirt cost</small>
          </div>
        )}
      </section>

      {/* Waiver Rules */}
      <section className="setup-section waiver-section">
        <h3>Special Cases / Waivers</h3>
        
        {/* No Admin Fee */}
        <div className="waiver-case">
          <h4>Case 1: No Admin Fee (R0)</h4>
          <p>When school doesn't charge admin fee at all</p>
          
          <div className="waiver-grid">
            <div className="form-group">
              <label>
                Brand Building:
                <input 
                  type="number" 
                  value={zeroFeeRules.bb}
                  onChange={(e) => setZeroFeeRules({...zeroFeeRules, bb: e.target.value})}
                  min="0"
                />
                <span className="currency">R{zeroFeeRules.bb}</span>
              </label>
            </div>
            
            <div className="form-group">
              <label>
                Franchisee Keeps:
                <input 
                  type="number" 
                  value={zeroFeeRules.franchisee}
                  onChange={(e) => setZeroFeeRules({...zeroFeeRules, franchisee: e.target.value})}
                  min="0"
                />
                <span className="currency">R{zeroFeeRules.franchisee}</span>
              </label>
            </div>
            
            <div className="form-group">
              <label>
                Head Office:
                <input 
                  type="number" 
                  value={zeroFeeRules.ho}
                  onChange={(e) => setZeroFeeRules({...zeroFeeRules, ho: e.target.value})}
                  min="0"
                />
                <span className="currency">R{zeroFeeRules.ho}</span>
              </label>
            </div>
          </div>
          
          <div className="form-group checkbox-group">
            <label>
              <input 
                type="checkbox"
                checked={zeroFeeRules.tshirt}
                onChange={(e) => setZeroFeeRules({...zeroFeeRules, tshirt: e.target.checked})}
              />
              Include T-shirt anyway (franchisee pays)
            </label>
          </div>
        </div>
        
        {/* Partial Fee (R40) */}
        <div className="waiver-case">
          <h4>Case 2: Partial Fee (R40 - No T-shirt)</h4>
          <p>When school doesn't want T-shirts for students</p>
          
          <div className="waiver-grid">
            <div className="form-group">
              <label>
                Brand Building:
                <input 
                  type="number" 
                  value={partial40Rules.bb}
                  onChange={(e) => setPartial40Rules({...partial40Rules, bb: e.target.value})}
                  min="0"
                />
                <span className="currency">R{partial40Rules.bb}</span>
              </label>
            </div>
            
            <div className="form-group">
              <label>
                Franchisee Keeps:
                <input 
                  type="number" 
                  value={partial40Rules.franchisee}
                  onChange={(e) => setPartial40Rules({...partial40Rules, franchisee: e.target.value})}
                  min="0"
                />
                <span className="currency">R{partial40Rules.franchisee}</span>
              </label>
            </div>
            
            <div className="form-group">
              <label>
                Head Office:
                <input 
                  type="number" 
                  value={partial40Rules.ho}
                  onChange={(e) => setPartial40Rules({...partial40Rules, ho: e.target.value})}
                  min="0"
                />
                <span className="currency">R{partial40Rules.ho}</span>
              </label>
            </div>
          </div>
          
          <div className="form-group checkbox-group">
            <label>
              <input 
                type="checkbox"
                checked={partial40Rules.tshirt}
                onChange={(e) => setPartial40Rules({...partial40Rules, tshirt: e.target.checked})}
              />
              Include T-shirt
            </label>
          </div>
          
          <div className="total-check">
            <strong>Partial Fee Total:</strong> R{(parseFloat(partial40Rules.bb) + parseFloat(partial40Rules.franchisee) + parseFloat(partial40Rules.ho)).toFixed(2)}
          </div>
        </div>
      </section>

      {/* Additional Pricing */}
      <section className="setup-section">
        <h3>Additional Product Pricing</h3>
        
        <div className="pricing-grid">
          <div className="form-group">
            <label>
              Adult T-shirts (Small - Large):
              <input 
                type="number" 
                value={adultSmallLarge}
                onChange={(e) => setAdultSmallLarge(e.target.value)}
                min="0"
              />
              <span className="currency">R{adultSmallLarge}</span>
            </label>
          </div>
          
          <div className="form-group">
            <label>
              Adult T-shirts (XL - XXXXL):
              <input 
                type="number" 
                value={adultXlUp}
                onChange={(e) => setAdultXlUp(e.target.value)}
                min="0"
              />
              <span className="currency">R{adultXlUp}</span>
            </label>
          </div>
          
          <div className="form-group">
            <label>
              Stickers (per roll of 500):
              <input 
                type="number" 
                value={stickerPrice}
                onChange={(e) => setStickerPrice(e.target.value)}
                min="0"
              />
              <span className="currency">R{stickerPrice}</span>
            </label>
          </div>
        </div>
      </section>

      {/* Royalty Settings */}
      <section className="setup-section">
        <h3>Royalty & VAT Settings</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label>
              Royalty Percentage (on term fees):
              <input 
                type="number" 
                value={royaltyPct}
                onChange={(e) => setRoyaltyPct(e.target.value)}
                min="0"
                max="100"
                step="0.1"
              />
              <span className="currency">{royaltyPct}%</span>
            </label>
          </div>
          
          <div className="form-group">
            <label>
              VAT Percentage:
              <input 
                type="number" 
                value={vatPct}
                onChange={(e) => setVatPct(e.target.value)}
                min="0"
                max="100"
                step="0.1"
              />
              <span className="currency">{vatPct}%</span>
            </label>
          </div>
        </div>
      </section>

      {/* Preview Examples */}
      <section className="setup-section preview-section">
        <h3>Preview Examples</h3>
        
        <div className="preview-grid">
          <div className="preview-card">
            <h4>Standard School (R{defaultAdminFee})</h4>
            <div className="preview-breakdown">
              <p>Brand Building: <strong>R{bbFund}</strong></p>
              <p>Franchisee: <strong>R{franchiseeKeeps}</strong></p>
              <p>Head Office: <strong>R{headOfficeGets}</strong></p>
              <p>T-shirt: <strong>{includesTshirt ? '✓ Included' : '✗ Not included'}</strong></p>
            </div>
          </div>
          
          <div className="preview-card">
            <h4>Waived School (R0)</h4>
            <div className="preview-breakdown">
              <p>Brand Building: <strong>R{zeroFeeRules.bb}</strong></p>
              <p>Franchisee: <strong>R{zeroFeeRules.franchisee}</strong></p>
              <p>Head Office: <strong>R{zeroFeeRules.ho}</strong></p>
              <p>T-shirt: <strong>{zeroFeeRules.tshirt ? '✓ Included' : '✗ Not included'}</strong></p>
            </div>
          </div>
          
          <div className="preview-card">
            <h4>Partial Fee School (R40)</h4>
            <div className="preview-breakdown">
              <p>Brand Building: <strong>R{partial40Rules.bb}</strong></p>
              <p>Franchisee: <strong>R{partial40Rules.franchisee}</strong></p>
              <p>Head Office: <strong>R{partial40Rules.ho}</strong></p>
              <p>T-shirt: <strong>{partial40Rules.tshirt ? '✓ Included' : '✗ Not included'}</strong></p>
            </div>
          </div>
        </div>
      </section>

      {/* Actions */}
      <div className="setup-actions">
        <button 
          onClick={handleSave} 
          className="save-btn"
          disabled={saving || !validateBreakdown()}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        
        {onClose && (
          <button onClick={onClose} className="cancel-btn" disabled={saving}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export default AdminFeeStructureSetup;